import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import { Card } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { EmailEditor } from './EmailEditor';
import { Mail, Send, Eye, Save, Plus, Trash2, Info, Loader2, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlaceholderList = ({ onInsert }) => {
  const [items, setItems] = useState([]);
  useEffect(() => { axios.get(`${API}/email/placeholders`).then(r => setItems(r.data.placeholders || [])); }, []);
  return (
    <div className="border border-slate-200 rounded-lg bg-slate-50 p-3" data-testid="placeholder-list">
      <div className="text-xs font-semibold text-[#1e1b4b] uppercase tracking-wider mb-2 flex items-center gap-1"><Info className="w-3.5 h-3.5" /> Placeholders</div>
      <div className="flex flex-wrap gap-1.5 max-h-56 overflow-auto">
        {items.map(p => (
          <button
            key={p.key}
            type="button"
            onClick={() => onInsert && onInsert(p.key)}
            title={`Voorbeeld: ${p.sample || '(leeg)'}`}
            className="text-xs font-mono px-2 py-1 rounded bg-white border border-slate-200 hover:bg-[#1e1b4b] hover:text-white transition-colors"
            data-testid={`placeholder-${p.key}`}
          >
            {`{${p.key}}`}
          </button>
        ))}
      </div>
      <div className="text-xs text-slate-500 mt-2">Klik een placeholder om te kopiëren naar je klembord. Ze worden bij verzending automatisch vervangen door klantdata.</div>
    </div>
  );
};

const TemplateEditor = ({ template, triggerId, onClose, onSaved, onDeleted }) => {
  const [name, setName] = useState(template?.name || '');
  const [subject, setSubject] = useState(template?.subject || '');
  const [htmlBody, setHtmlBody] = useState(template?.html_body || '');
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [preview, setPreview] = useState(null);
  const isNew = !template?.id;

  const copyPlaceholder = (key) => {
    const token = `{${key}}`;
    navigator.clipboard?.writeText(token);
    toast.success(`${token} gekopieerd`);
  };

  const save = async () => {
    if (!name.trim() || !subject.trim()) { toast.error('Naam en onderwerp zijn verplicht'); return; }
    setSaving(true);
    try {
      let saved;
      if (isNew) {
        const res = await axios.post(`${API}/email/templates`, { trigger: triggerId, name, subject, html_body: htmlBody });
        saved = res.data;
      } else {
        const res = await axios.patch(`${API}/email/templates/${template.id}`, { name, subject, html_body: htmlBody });
        saved = res.data;
      }
      toast.success('Template opgeslagen');
      onSaved && onSaved(saved);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Opslaan mislukt');
    } finally { setSaving(false); }
  };

  const openPreview = async () => {
    try {
      const res = await axios.post(`${API}/email/preview`, { recipient_email: 'preview@voorbeeld.nl', subject, html_body: htmlBody });
      setPreview(res.data);
    } catch (e) { toast.error('Preview mislukt'); }
  };

  const sendTest = async () => {
    if (!testEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) { toast.error('Ongeldig e-mailadres'); return; }
    setSendingTest(true);
    try {
      await axios.post(`${API}/email/test`, { recipient_email: testEmail, subject, html_body: htmlBody });
      toast.success(`Testmail verstuurd naar ${testEmail}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Testmail mislukt');
    } finally { setSendingTest(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Template "${template.name}" verwijderen?`)) return;
    try {
      await axios.delete(`${API}/email/templates/${template.id}`);
      toast.success('Template verwijderd');
      onDeleted && onDeleted(template.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Verwijderen mislukt');
    }
  };

  return (
    <div className="space-y-4" data-testid="template-editor">
      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-600">Templatenaam</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Bijv. Standaardbevestiging" data-testid="tpl-name-input" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-600">Onderwerp</Label>
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Bevestiging aanmelding {zaaknummer} – Bezwaarmeesters" data-testid="tpl-subject-input" />
      </div>
      <div className="grid md:grid-cols-[1fr_260px] gap-4">
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-600 mb-1 block">Inhoud (WYSIWYG)</Label>
          <EmailEditor value={htmlBody} onChange={setHtmlBody} />
        </div>
        <PlaceholderList onInsert={copyPlaceholder} />
      </div>

      <Card className="p-4 border-slate-200 bg-slate-50">
        <div className="font-semibold text-[#1e1b4b] text-sm mb-3 flex items-center gap-2"><Send className="w-4 h-4" /> Testmail versturen</div>
        <div className="flex gap-2">
          <Input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="jouw@e-mail.nl" data-testid="test-email-input" />
          <Button variant="outline" onClick={openPreview} data-testid="preview-btn"><Eye className="w-4 h-4 mr-2" /> Preview</Button>
          <Button className="bg-[#f26b1f] hover:bg-[#d85a12] text-white" onClick={sendTest} disabled={sendingTest} data-testid="send-test-btn">
            {sendingTest ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Verstuur test
          </Button>
        </div>
        <div className="text-xs text-slate-500 mt-2">De testmail wordt verstuurd met voorbeelddata (Jan de Vries, zaak 2026BM9999).</div>
      </Card>

      <div className="flex justify-between pt-2 border-t border-slate-200">
        <div>
          {!isNew && !template?.is_default && (
            <Button variant="outline" onClick={remove} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="delete-tpl-btn"><Trash2 className="w-4 h-4 mr-2" /> Verwijderen</Button>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onClose}>Annuleren</Button>
          <Button className="bg-[#1e1b4b] hover:bg-[#2a2464]" onClick={save} disabled={saving} data-testid="save-tpl-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Opslaan
          </Button>
        </div>
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Preview met voorbeelddata</DialogTitle>
            <DialogDescription>Zo ziet de e-mail eruit met een fictieve klant. Bij verzending wordt dit vervangen door de echte klantdata.</DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="space-y-4">
              <div className="border-b border-slate-200 pb-3">
                <div className="text-xs text-slate-500 uppercase tracking-wider">Onderwerp</div>
                <div className="font-semibold text-[#1e1b4b]">{preview.subject}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">HTML weergave</div>
                <div className="border border-slate-200 rounded-lg p-4 bg-white prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: preview.html }} />
              </div>
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Plain text weergave</div>
                <pre className="border border-slate-200 rounded-lg p-3 bg-slate-50 text-xs text-slate-700 whitespace-pre-wrap font-sans">{preview.text}</pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const EmailSettings = () => {
  const [triggers, setTriggers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { triggerId, template? }

  const load = async () => {
    setLoading(true);
    try {
      const [tRes, tplRes] = await Promise.all([axios.get(`${API}/email/triggers`), axios.get(`${API}/email/templates`)]);
      setTriggers(tRes.data); setTemplates(tplRes.data);
    } catch (e) { toast.error('Laden mislukt'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const templatesFor = (trigId) => templates.filter(t => t.trigger === trigId);

  const toggleEnabled = async (t) => {
    try {
      await axios.patch(`${API}/email/triggers/${t.id}`, { enabled: !t.enabled });
      setTriggers(prev => prev.map(x => x.id === t.id ? { ...x, enabled: !x.enabled } : x));
      toast.success(`Trigger ${!t.enabled ? 'ingeschakeld' : 'uitgeschakeld'}`);
    } catch (e) { toast.error('Kon niet wijzigen'); }
  };

  const setDelay = async (t, hours) => {
    const val = Math.max(0, parseInt(hours || 0));
    try {
      await axios.patch(`${API}/email/triggers/${t.id}`, { delay_hours: val });
      setTriggers(prev => prev.map(x => x.id === t.id ? { ...x, delay_hours: val } : x));
    } catch (e) { toast.error('Kon delay niet opslaan'); }
  };

  const setActiveTemplate = async (t, tplId) => {
    try {
      await axios.patch(`${API}/email/triggers/${t.id}`, { active_template_id: tplId });
      const tpl = templates.find(x => x.id === tplId);
      setTriggers(prev => prev.map(x => x.id === t.id ? { ...x, active_template_id: tplId, active_template_name: tpl?.name } : x));
      toast.success('Actieve template gewijzigd');
    } catch (e) { toast.error('Kon niet wijzigen'); }
  };

  if (loading) return <div className="p-8 text-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="email-settings-tab">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900">
          Zet triggers hier aan of uit, kies welke template gebruikt wordt en stel eventueel een vertraging in.
          Elke trigger kan meerdere templates hebben (bijv. A/B-varianten). Klik op een template om te bewerken.
        </div>
      </div>

      {triggers.map(t => {
        const tpls = templatesFor(t.id);
        return (
          <Card key={t.id} className="p-5 border border-slate-200" data-testid={`trigger-card-${t.id}`}>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#1e1b4b]/10 flex items-center justify-center flex-shrink-0"><Mail className="w-5 h-5 text-[#1e1b4b]" /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="font-semibold text-[#1e1b4b]">{t.label}</div>
                  {t.enabled ? <Badge className="bg-emerald-500">Actief</Badge> : <Badge variant="outline">Uit</Badge>}
                </div>
                <div className="text-xs text-slate-500 mt-1">{t.description}</div>

                <div className="grid md:grid-cols-3 gap-3 mt-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-600">Verzenden</Label>
                    <div className="flex items-center gap-2 mt-1.5">
                      <Switch checked={t.enabled} onCheckedChange={() => toggleEnabled(t)} data-testid={`toggle-${t.id}`} />
                      <span className="text-sm text-slate-700">{t.enabled ? 'Aan' : 'Uit'}</span>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-600">Vertraging (uren)</Label>
                    <Input type="number" min="0" defaultValue={t.delay_hours || 0} onBlur={(e) => setDelay(t, e.target.value)} className="mt-1.5" data-testid={`delay-${t.id}`} />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-600">Actieve template</Label>
                    <Select value={t.active_template_id || ''} onValueChange={(v) => setActiveTemplate(t, v)}>
                      <SelectTrigger className="mt-1.5" data-testid={`active-tpl-${t.id}`}><SelectValue placeholder="Kies template" /></SelectTrigger>
                      <SelectContent>{tpls.map(tp => <SelectItem key={tp.id} value={tp.id}>{tp.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mt-4 border-t border-slate-100 pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Templates voor deze trigger</div>
                    <Button size="sm" variant="outline" onClick={() => setEditing({ triggerId: t.id, template: null })} data-testid={`new-tpl-${t.id}`}><Plus className="w-3.5 h-3.5 mr-1" /> Nieuwe template</Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {tpls.map(tp => (
                      <button key={tp.id} type="button" onClick={() => setEditing({ triggerId: t.id, template: tp })} className="text-xs px-2.5 py-1.5 rounded border border-slate-200 hover:border-[#1e1b4b] hover:bg-slate-50 flex items-center gap-1.5" data-testid={`edit-tpl-${tp.id}`}>
                        <FileText className="w-3 h-3" /> {tp.name}
                        {tp.is_default && <Badge variant="outline" className="text-[10px] ml-1">standaard</Badge>}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        );
      })}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{editing?.template ? `Template bewerken: ${editing.template.name}` : 'Nieuwe template'}</DialogTitle>
            <DialogDescription>Gebruik placeholders in de vorm <code className="bg-slate-100 px-1 rounded">{'{klantnaam}'}</code>. Zie de lijst rechts.</DialogDescription>
          </DialogHeader>
          {editing && (
            <TemplateEditor
              triggerId={editing.triggerId}
              template={editing.template}
              onClose={() => setEditing(null)}
              onSaved={() => { setEditing(null); load(); }}
              onDeleted={() => { setEditing(null); load(); }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailSettings;
